import axios from 'axios';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { CONFIG } from 'variables/config';

const MONTH_NAMES = [
    '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const UpdateSellerInvoiceInformation = () => {
    const [user, setUser] = useState(null);
    const [defaultUser, setDefaultUser] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const nav = useNavigate();

    const initUser = () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/me`, config)
            .then((res) => {
                const user = res.data;
                setUser(user);
                setDefaultUser(user);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const fetchInvoices = () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        setLoadingInvoices(true);
        axios
            .get(`${CONFIG.apiUrl}/seller/invoices`, config)
            .then((res) => {
                setInvoices(res.data.invoices || []);
                setLoadingInvoices(false);
            })
            .catch((err) => {
                console.log(err);
                setLoadingInvoices(false);
            });
    };

    const handleChange = (e: any) => {
        const { id, value } = e.target;
        setUser({ ...user, [id]: value });
    };

    const updateProfil = () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        const data = {
            company: user.company,
            address: user.address,
            vatNumber: user.vatNumber,
            siren: user.siren,
        };
        axios
            .put(`${CONFIG.apiUrl}/user`, data, config)
            .then((res) => {
                toast.success('Votre profil a été mis à jour');
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const downloadInvoice = (year: number, month: number) => {
        const token = localStorage.getItem('token');
        window.open(
            `${CONFIG.apiUrl}/seller/invoice/${year}/${month}?token=${encodeURI(token)}`,
            '_blank'
        );
    };

    const areInvoiceInfoValid = () => {
        return user?.siren && user?.company && user?.address;
    };

    useEffect(() => {
        initUser();
        fetchInvoices();
    }, []);

    return (
        <>
            <Card extra={'w-full px-4 py-4 border-2'}>
                <ToastContainer />
                {/* Header */}
                <div className="w-full px-1">
                    <h4 className="text-base font-bold text-navy-700 dark:text-white">
                        Information de facturation
                    </h4>
                </div>
                {/* inputs */}
                <div className="mt-4 grid grid-cols-2 gap-2">
                    <InputField
                        extra="mb-2"
                        label="Mon entreprise"
                        placeholder="Mon entreprise"
                        defaultValue={user?.company}
                        id="company"
                        type="text"
                        onChange={handleChange}
                    />
                    <InputField
                        extra="mb-2"
                        label="Adresse"
                        placeholder="17 rue de la paix, 75000 Paris France"
                        defaultValue={user?.address}
                        id="address"
                        type="text"
                        onChange={handleChange}
                    />
                    <InputField
                        extra="mb-2"
                        label="Numéro de TVA"
                        placeholder="FR12345678901"
                        defaultValue={user?.vatNumber}
                        id="vatNumber"
                        type="text"
                        onChange={handleChange}
                    />
                    <InputField
                        extra="mb-2"
                        label="Numéro de SIREN"
                        placeholder="123456789"
                        defaultValue={user?.siren}
                        id="siren"
                        type="text"
                        onChange={handleChange}
                    />
                </div>
                <div className="mt-3 flex w-full justify-end">
                    <button
                        className="linear rounded-lg bg-leadrs px-4 py-1.5 text-sm font-medium text-white transition duration-200 hover:cursor-pointer hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                        onClick={() => updateProfil()}
                    >
                        Sauvegarder
                    </button>
                </div>
            </Card>

            {/* Monthly Invoices List */}
            <Card extra={'w-full mt-4 px-4 py-4 border-2'}>
                <div className="w-full px-1">
                    <div className="flex items-center justify-between">
                        <h4 className="text-base font-bold text-navy-700 dark:text-white">
                            Mes factures
                        </h4>
                        <button
                            className="linear rounded-lg bg-leadrs px-3 py-1.5 text-xs font-medium text-white transition duration-200 hover:cursor-pointer hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                            onClick={() => fetchInvoices()}
                            disabled={loadingInvoices}
                        >
                            {loadingInvoices ? 'Chargement...' : 'Actualiser'}
                        </button>
                    </div>
                </div>

                <div className="mt-4">
                    {!areInvoiceInfoValid() && (
                        <div className="mb-3 rounded-lg bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                            Veuillez compléter vos informations de facturation ci-dessus pour pouvoir télécharger vos factures.
                        </div>
                    )}
                    {loadingInvoices ? (
                        <div className="flex justify-center py-4">
                            <p className="text-sm text-gray-600">Chargement des factures...</p>
                        </div>
                    ) : invoices.length === 0 ? (
                        <div className="flex justify-center py-4">
                            <p className="text-sm text-gray-600">Aucune facture disponible</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-navy-700 dark:text-white">
                                            Période
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-navy-700 dark:text-white">
                                            Nombre de leads
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-navy-700 dark:text-white">
                                            Montant HT
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-navy-700 dark:text-white">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((invoice: any) => (
                                        <tr
                                            key={`${invoice.year}-${invoice.month}`}
                                            className="border-b border-gray-100 hover:bg-gray-50 dark:hover:bg-navy-700"
                                        >
                                            <td className="px-3 py-2 text-xs text-navy-700 dark:text-white">
                                                {MONTH_NAMES[invoice.month]} {invoice.year}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                                                {invoice.totalLeads}
                                            </td>
                                            <td className="px-3 py-2 text-xs font-medium text-navy-700 dark:text-white">
                                                {invoice.totalAmountHT.toFixed(2)} EUR
                                            </td>
                                            <td className="px-3 py-2 text-xs">
                                                <button
                                                    onClick={() => downloadInvoice(invoice.year, invoice.month)}
                                                    disabled={!areInvoiceInfoValid()}
                                                    className={`underline ${
                                                        areInvoiceInfoValid()
                                                            ? 'text-leadrs hover:text-brand-600'
                                                            : 'cursor-not-allowed text-gray-400'
                                                    }`}
                                                >
                                                    Télécharger PDF
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Card>
        </>
    );
};

export default UpdateSellerInvoiceInformation;
